// frontend/lib/diagnostics/swProbe.js
//
// Compare page/origin builds and identify legacy caching workers. The minimal
// network-only worker has its own version, independent of the application build.
// A page/origin mismatch can simply mean an older tab is still open.
//
// Web-only — returns a "not applicable" result on native.

import { Platform } from 'react-native';
import { BUILD_ID } from '@/constants/config';

export async function probeServiceWorker() {
    if (Platform.OS !== 'web') {
        return { applicable: false, registered: null, note: 'Service worker probe is web-only.' };
    }

    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (!nav?.serviceWorker) {
        return { applicable: true, registered: false, note: 'Service workers not supported.' };
    }

    let registration = null;
    let scriptURL = null;
    let state = null;
    let scope = null;

    try {
        registration = await nav.serviceWorker.getRegistration();
        if (registration) {
            scriptURL = registration.active?.scriptURL || registration.waiting?.scriptURL || registration.installing?.scriptURL || null;
            state = registration.active?.state || registration.waiting?.state || registration.installing?.state || null;
            scope = registration.scope || null;
        }
    } catch (_) { /* getRegistration can throw in rare browser states */ }

    const registered = !!registration;
    const controller = nav.serviceWorker.controller;
    const controllerURL = controller?.scriptURL || null;

    // Fetch /build.json (never SW-cached — sw.js bypasses this path).
    let originBuildId = null;
    let originFetchOk = null;
    try {
        const res = await fetch('/build.json', { cache: 'no-store' });
        originFetchOk = res.ok;
        if (res.ok) {
            const data = await res.json();
            originBuildId = data.buildId || null;
        }
    } catch (_) {
        originFetchOk = false;
    }

    // Both legacy and minimal workers answer GET_VERSION.
    let swBuildId = null;
    let workerMode = null;
    let workerVersion = null;
    if (controller) {
        try {
            const versionData = await new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    nav.serviceWorker.removeEventListener('message', handler);
                    resolve(null);
                }, 3000);
                const handler = (event) => {
                    if (event.data?.type === 'VERSION') {
                        clearTimeout(timeout);
                        nav.serviceWorker.removeEventListener('message', handler);
                        resolve(event.data);
                    }
                };
                nav.serviceWorker.addEventListener('message', handler);
                controller.postMessage({ type: 'GET_VERSION' });
            });
            workerMode = versionData?.mode || null;
            workerVersion = versionData?.version || null;
            // Only legacy workers have a cache tied to an application build.
            if (versionData?.cacheName) {
                const m = versionData.cacheName.match(/gab-ai-([0-9a-f]+)/);
                swBuildId = m ? m[1] : versionData.cacheName;
            }
        } catch (_) { /* SW unresponsive — leave null */ }
    }

    // Fallback: check cached HTML for a build ID stamp (covers the case where
    // postMessage fails — SW cold-starting, controller not yet available, etc.)
    if (!swBuildId && workerMode !== 'network-only') {
        try {
            const cachedResponse = await caches.match(location.href)
                || await caches.match(location.pathname)
                || await caches.match('/');
            if (cachedResponse) {
                const html = await cachedResponse.text();
                const match = html.match(/__GAB_BUILD_ID__['"]?\s*[:=]\s*['"]([0-9a-f]+)['"]/i);
                swBuildId = match ? match[1] : null;
            }
        } catch (_) { /* leave null */ }
    }

    // Compute version skew — any mismatch among the three known values.
    const bundleBuildId = BUILD_ID;
    const knownValues = [bundleBuildId, originBuildId, swBuildId].filter((v) => v != null);
    const allSame = knownValues.length >= 2 && knownValues.every((v) => v === knownValues[0]);
    const versionSkew = knownValues.length >= 2 && !allSame;

    return {
        applicable: true,
        registered,
        scriptURL: scriptURL || controllerURL,
        state,
        scope,
        registrationAgeMs: registration?.installationTime
            ? Date.now() - new Date(registration.installationTime).getTime()
            : null,
        versionSkew,
        workerMode,
        workerVersion,
        buildIds: {
            bundle: bundleBuildId,
            origin: originBuildId,
            sw: swBuildId,
        },
        originFetchOk,
    };
}

// ---------------------------------------------------------------------------
// Fix actions — called from the SW probe card when skew or staleness is
// detected. Each is a progressive escalation: soft refresh first, nuclear
// reset second.
// ---------------------------------------------------------------------------

/**
 * Soft refresh: clears the SW's asset cache, forces an update check for a new
 * sw.js, and if one is waiting, activates it immediately (skipWaiting). Then
 * reloads the page so the next load fetches fresh assets.
 *
 * Retained for migration from legacy workers. The minimal replacement has no
 * asset cache, and activates itself when downloaded.
 */
export async function clearCacheAndUpdateSW() {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.serviceWorker) return;

    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
        window.location.reload();
        return;
    }

    // 1. Tell the active SW to clear its cache now.
    if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
    }

    // Also clear caches directly from the window (in case the SW is
    // unresponsive / cold-starting). The SW and window share the same Cache
    // API storage.
    try {
        const cacheNames = await caches.keys();
        await Promise.all(
            cacheNames
                .filter((name) => name.startsWith('gab-ai-')) // don't nuke unrelated caches
                .map((name) => caches.delete(name))
        );
    } catch (_) { /* best-effort */ }

    // 2. Force-check for a new sw.js. The browser normally only checks once
    //    per hour (and only when the tab is visible). update() checks now.
    try {
        await reg.update();
    } catch (_) { /* can fail if the page doesn't have focus — non-critical */ }

    // 3. If a new SW installed and is waiting to activate, tell it to skip
    //    the waiting state and take control immediately.
    if (reg.waiting && reg.waiting.state === 'installed') {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        }, { once: true });
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Safety net: reload after 5s even if controllerchange never fires.
        setTimeout(() => window.location.reload(), 5000);
        return;
    }

    // No waiting SW (sw.js hasn't changed). The cache clear above is the fix
    // — reload so assets are re-fetched from the network.
    setTimeout(() => window.location.reload(), 500);
}

/**
 * Nuclear reset: completely unregisters the service worker and clears ALL
 * caches. Use when the soft refresh didn't resolve the issue, or when the SW
 * itself is corrupt / stuck in a bad state.
 *
 * After unregister, the next page load has NO service worker. The normal
 * registration code in +html.jsx re-registers it from scratch.
 */
export async function unregisterServiceWorker() {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined') return;

    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));

    // Clear ALL caches (not just gab-ai-* — nuclear).
    try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
    } catch (_) { /* best-effort */ }

    window.location.reload();
}
